import { useAppCatalogLocale, useAppLocale } from '@/i18n/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { formatCount } from '@/i18n/format';
import pageCatalog from '@/i18n/ns/connections-page';
import accessCatalog from '@/i18n/ns/connection-access';
import connectionsCatalog from '@/i18n/ns/connections';
import type { ConnectionRow, ConnectionRowState } from './rows';

/** The words every Connections surface uses for a row: who, what, state and when. */
export function useConnectionWords(companyName: string) {
  const copy = useNamespaceCopy(pageCatalog);
  const access = useNamespaceCopy(accessCatalog);
  const connections = useNamespaceCopy(connectionsCatalog);
  const catalogLocale = useAppCatalogLocale() ?? 'en';
  const locale = useAppLocale();
  const company = companyName || connections.agentCompanyFallback;
  const count = (forms: string, value: number) => formatCount(forms, value, catalogLocale);
  const state = (value: ConnectionRowState) =>
    ({
      connected: copy.stateConnected,
      attention: copy.stateAttention,
      review: copy.stateReview,
      unavailable: copy.stateUnavailable,
      off: copy.stateOff,
    })[value];
  const who = (row: Pick<ConnectionRow, 'audience' | 'read'>) => {
    if (row.audience === 'company') return formatCopy(access.whoEveryone, { company });
    if (row.audience === 'people')
      return count(
        access.peopleNoun,
        (row.read?.policy?.people.length ?? 0) + (row.read?.policy?.ownerUserId ? 1 : 0),
      );
    return access.whoOnlyYou;
  };
  const what = (row: Pick<ConnectionRow, 'changes'>) =>
    row.changes === 'unknown'
      ? '—'
      : row.changes === 'write'
        ? access.whatReadWrite
        : access.whatRead;
  const date = (value: string | null | undefined) => {
    const parsed = value ? new Date(value) : null;
    return parsed && Number.isFinite(parsed.getTime())
      ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(parsed)
      : null;
  };
  const ago = (value: string | null | undefined, now = Date.now()) => {
    const parsed = value ? new Date(value).getTime() : NaN;
    if (!Number.isFinite(parsed)) return null;
    const minutes = Math.round((parsed - now) / 60_000);
    const format = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'short' });
    if (Math.abs(minutes) < 60) return format.format(minutes, 'minute');
    const hours = Math.round(minutes / 60);
    if (Math.abs(hours) < 24) return format.format(hours, 'hour');
    const days = Math.round(hours / 24);
    return Math.abs(days) < 7 ? format.format(days, 'day') : date(value);
  };
  return { copy, access, company, count, state, who, what, date, ago, locale };
}

export type ConnectionWords = ReturnType<typeof useConnectionWords>;

/** The line under a row's name: the account, server address or tables that tell rows apart. */
export function rowIdentity(row: ConnectionRow, words: ConnectionWords) {
  if (row.kind === 'database') {
    const tables = row.read?.access.resources.length ?? 0;
    return tables
      ? `${words.copy.kindDatabase} · ${words.count(words.copy.tablesNoun, tables)}`
      : words.copy.kindDatabase;
  }
  // A Shopify Developer App store is known by its store address; any other server by its kind.
  if (row.server?.authType === 'shopify_dev_app') return row.identity;
  if (row.kind === 'server')
    return row.identity ? `${words.copy.kindServer} · ${row.identity}` : words.copy.kindServer;
  const accounts = row.item?.readiness?.accountCount;
  if (row.identity) return row.identity;
  if (accounts !== undefined) return words.count(words.copy.adAccountsNoun, accounts);
  return null;
}
